import React, { useState, useEffect, useRef } from "react";
import {
  Space,
  Divider,
  Typography,
  Select,
  Switch,
  Badge,
  DatePicker,
  Button,
  theme,
  Tag,
  Popconfirm,
  Popover,
  Input,
  Modal,
  Drawer,
  Checkbox,
  Tooltip,
  Dropdown,
} from "antd";
import {
  SettingOutlined,
  TagOutlined,
  AlertOutlined,
  AppstoreOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  TeamOutlined,
  UserOutlined,
  CloseOutlined,
  CalendarOutlined,
  LinkOutlined,
  ArrowRightOutlined,
  ShopOutlined,
  EditOutlined,
  NumberOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import TicketServices, {
  ITicket,
  TicketStatus,
  TicketPriority,
  TicketModule,
  CloseReason,
  CLOSED_REASONS,
  DateFormats,
  TicketDependencyType,
} from "../../../Services/TicketServices";
import { getStatusTag, getPriorityTag } from "./utils";
import { adminContext } from "../../../Common/PageRoute";

const { Text } = Typography;

var relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime)

interface TicketPropertiesProps {
  ticket: ITicket;
  submitting: boolean;
  assigneeLoading: boolean;
  assigneeOptions: Array<{ label: string; value: string }>;
  onAssignChange: (userId: string) => Promise<void>;
  onStatusTransitionConfirm: (nextStatus: number, reason: string, updateLinked?: boolean) => Promise<void>;
  onResolutionETAChange: (date: any, reason?: string) => Promise<void>;
  onDueDateChange: (date: any, reason?: string) => Promise<void>;
  onInputRequiredToggle: (checked: boolean, reason: string) => Promise<void>;
  onCloseConfirm: (reason: string, updateLinked?: boolean) => Promise<void>;
  onReopenConfirm: (reason: string) => Promise<void>;
  onPriorityChange: (priority: number) => Promise<void>;
  onModuleChange: (module: number) => Promise<void>;
  onUnmergeConfirm?: () => Promise<void>;
  onUnlinkConfirm?: (removeAllInterconnected: boolean) => Promise<void>;
  onCollaboratorsChange?: (userIds: string[]) => Promise<void>;
  onRefresh?: () => Promise<void>;
  shiftModalTrigger?: number;
}

export const TicketProperties: React.FC<TicketPropertiesProps> = ({
  ticket,
  submitting,
  assigneeLoading,
  assigneeOptions,
  onAssignChange,
  onStatusTransitionConfirm,
  onResolutionETAChange,
  onDueDateChange,
  onInputRequiredToggle,
  onCloseConfirm,
  onReopenConfirm,
  onPriorityChange,
  onModuleChange,
  onUnmergeConfirm,
  onUnlinkConfirm,
  onCollaboratorsChange,
  onRefresh,
  shiftModalTrigger,
}) => {
  const { token } = theme.useToken();

  const premiumShadow = token.boxShadowTertiary;
  const subtleBorder = `1px solid ${token.colorBorderSecondary}`;

  // Local UI States for inline confirmations
  type PopupType = "status" | "priority" | "module" | "inputRequired" | "eta" | "dueDate" | "assignAgent" | "close" | "reopen" | "shift" | "unlink" | "collaborators" | "number";
  const [activePopup, setActivePopup] = useState<PopupType | null>(null);

  const [tempNumberValue, setTempNumberValue] = useState(ticket?.number || "");
  useEffect(() => {
    setTempNumberValue(ticket?.number || "");
  }, [ticket?.number]);

  const [updateLinkedTickets, setUpdateLinkedTickets] = useState(false);

  const [tempAgentId, setTempAgentId] = useState<string | undefined>(undefined);
  const [tempAgentName, setTempAgentName] = useState("");

  const [tempCollaboratorIds, setTempCollaboratorIds] = useState<string[]>([]);
  useEffect(() => {
    if (ticket?.supportCollaborators) {
      setTempCollaboratorIds(ticket.supportCollaborators.map(c => String(c.id)));
    } else {
      setTempCollaboratorIds([]);
    }
  }, [ticket?.supportCollaborators]);

  const [tempEtaDate, setTempEtaDate] = useState<any>(null);
  const [etaReason, setEtaReason] = useState("");
  const [tempDueDate, setTempDueDate] = useState<any>(null);
  const [dueDateReason, setDueDateReason] = useState("");

  const [inputRequiredReason, setInputRequiredReason] = useState("");
  const [tempInputChecked, setTempInputChecked] = useState(false);

  // Inline Popover States for major workflow actions
  const [tempStatusValue, setTempStatusValue] = useState<number | null>(null);
  const [statusReason, setStatusReason] = useState("");
  const [selectedCloseReason, setSelectedCloseReason] = useState<CloseReason>(CloseReason.Unknown);

  const [closeReason, setCloseReason] = useState("");

  const [reopenReason, setReopenReason] = useState("");

  // Shift to CapsiTask states
  const { openNotification } = React.useContext(adminContext);
  const [shifting, setShifting] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskPriority, setTaskPriority] = useState<number>(1);
  const [taskDueDate, setTaskDueDate] = useState<dayjs.Dayjs | null>(null);

  const [capsiProjects, setCapsiProjects] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [capsiAssignees, setCapsiAssignees] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);

  const [capsiTypes, setCapsiTypes] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const [capsiTags, setCapsiTags] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const [capsiBranches, setCapsiBranches] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  const [selectedReportingToIds, setSelectedReportingToIds] = useState<string[]>([]);

  useEffect(() => {
    const currentId = ticket?.assignedTo?.id;
    setTempAgentId(currentId ? String(currentId) : undefined);
  }, [ticket?.assignedTo, ticket?.id]);

  useEffect(() => {
    if (shiftModalTrigger && shiftModalTrigger > 0) {
      handleOpenShiftModal();
    }
  }, [shiftModalTrigger]);

  const handleOpenShiftModal = async () => {
    setActivePopup("shift");
    setTaskTitle(ticket.title || "");
    // Default fallback to plain text if rich-text body, or simple concat
    setTaskDescription(ticket.message || "");

    // Map ticket priority (P1 = Emergency -> High, P3 = Normal -> Medium, etc)
    let priorityVal = 1; // Medium
    if (ticket.priority === TicketPriority.P1) {
      priorityVal = 3; // High
    } else if (ticket.priority === TicketPriority.P2) {
      priorityVal = 2; // High
    } else if (ticket.priority === TicketPriority.P4) {
      priorityVal = 0; // Low
    }
    setTaskPriority(priorityVal);
    setTaskDueDate(ticket.resolutionETA ? dayjs(ticket.resolutionETA) : dayjs().add(7, "day"));
    setSelectedProjectId(undefined);
    setSelectedAssigneeIds([]);
    setSelectedTypeIds([]);
    setSelectedTagIds([]);
    setSelectedBranchIds([]);
    setSelectedReportingToIds([]);

    setLoadingProjects(true);
    setLoadingTypes(true);
    setLoadingTags(true);
    setLoadingBranches(true);

    try {
      const [projRes, typeRes, tagRes, branchRes] = await Promise.all([
        TicketServices.getCapsiTaskProjects(),
        TicketServices.getCapsiTaskTypes(),
        TicketServices.getCapsiTaskTags(),
        TicketServices.getCapsiTaskBranches(),
      ]);

      if (projRes && Array.isArray(projRes)) {
        const mappedProjects = projRes.map((p: any) => ({
          label: p.label || p.Name || "Unnamed Project",
          value: String(p.value || p.Id),
        }));
        mappedProjects.sort((a, b) => a.label.localeCompare(b.label));
        setCapsiProjects(mappedProjects);
      }
      if (typeRes && Array.isArray(typeRes)) {
        setCapsiTypes(typeRes.map((t: any) => ({
          label: t.label || t.Label || t.Name || "Unnamed Type",
          value: String(t.value || t.Value || t.Id),
        })));
      }
      if (tagRes && Array.isArray(tagRes)) {
        setCapsiTags(tagRes.map((t: any) => ({
          label: t.label || t.Label || t.Name || "Unnamed Tag",
          value: String(t.value || t.Value || t.Id),
        })));
      }
      if (branchRes && Array.isArray(branchRes)) {
        setCapsiBranches(branchRes.map((b: any) => ({
          label: b.label || b.Label || b.Name || "Unnamed Branch",
          value: String(b.value || b.Value || b.Id),
        })));
      }
    } catch (error) {
      openNotification("error", "Error loading CapsiTask metadata options.");
    } finally {
      setLoadingProjects(false);
      setLoadingTypes(false);
      setLoadingTags(false);
      setLoadingBranches(false);
    }
  };

  const handleProjectChange = async (projectId: string) => {
    setSelectedProjectId(projectId);
    setSelectedAssigneeIds([]);
    setSelectedReportingToIds([]);
    setCapsiAssignees([]);
    if (!projectId) return;

    setLoadingAssignees(true);
    try {
      const res = await TicketServices.getCapsiTaskAssignees(projectId);
      if (res?.status && res.result?.items) {
        const options = res.result.items.map((a: any) => ({
          label: a.label || a.Label || a.Name || "Unnamed User",
          value: String(a.value || a.Value || a.Id),
        }));
        setCapsiAssignees(options);
      } else {
        openNotification("error", res?.message || "Failed to load project assignees.");
      }
    } catch (error) {
      openNotification("error", "Error loading project assignees.");
    } finally {
      setLoadingAssignees(false);
    }
  };

  const handleShiftConfirm = async () => {
    if (!selectedProjectId) return;
    setShifting(true);

    const branchesPayload = selectedBranchIds.map(id => {
      const matched = capsiBranches.find(b => b.value === id);
      return {
        Id: id,
        Name: matched ? matched.label : "Unnamed Branch"
      };
    });

    try {
      const res = await TicketServices.shiftToCapsiTask(ticket.id, {
        projectId: selectedProjectId,
        assigneeIds: selectedAssigneeIds,
        typeIds: selectedTypeIds,
        tagIds: selectedTagIds,
        branches: branchesPayload,
        reportingToIds: selectedReportingToIds,
        title: taskTitle,
        description: taskDescription,
        priority: taskPriority,
        dueDate: taskDueDate ? taskDueDate.toISOString() : undefined,
      });

      if (res?.status) {
        openNotification("success", res.message || "Ticket shifted to Capsitask successfully.");
        setActivePopup(null);
        if (onRefresh) {
          await onRefresh();
        }
      } else {
        openNotification("error", res?.message || "Failed to shift ticket.");
      }
    } catch (error) {
      openNotification("error", "Error shifting ticket.");
    } finally {
      setShifting(false);
    }
  };

  // Local UI States for inline confirmations (Priority, Module, Level)
  const [tempPriorityValue, setTempPriorityValue] = useState<number | null>(null);
  const [tempPriorityName, setTempPriorityName] = useState("");

  const [tempModuleValue, setTempModuleValue] = useState<number | null>(null);
  const [tempModuleName, setTempModuleName] = useState("");


  const allowedTransitionsMap: Record<number, number[]> = {
    [TicketStatus.Open]: [TicketStatus.Closed, TicketStatus.UnderResearch, TicketStatus.InProgress, TicketStatus.InTesting, TicketStatus.ReadyForRelease],
    [TicketStatus.Closed]: [],
    [TicketStatus.UnderResearch]: [TicketStatus.Closed, TicketStatus.InProgress, TicketStatus.InTesting, TicketStatus.ReadyForRelease],
    [TicketStatus.InProgress]: [TicketStatus.Closed, TicketStatus.UnderResearch, TicketStatus.InTesting, TicketStatus.ReadyForRelease],
    [TicketStatus.InTesting]: [TicketStatus.Closed, TicketStatus.UnderResearch, TicketStatus.InProgress, TicketStatus.ReadyForRelease],
    [TicketStatus.ReadyForRelease]: [TicketStatus.Closed, TicketStatus.UnderResearch, TicketStatus.InProgress, TicketStatus.InTesting],
  };

  const getAvailableTransitions = () => {
    const transitionIds = allowedTransitionsMap[ticket.status] || [];
    const labelsMap: Record<number, string> = {
      [TicketStatus.Open]: "Open",
      [TicketStatus.InProgress]: "In progress",
      [TicketStatus.UnderResearch]: "Under research",
      [TicketStatus.InTesting]: "Testing",
      [TicketStatus.ReadyForRelease]: "Ready for release",
      [TicketStatus.Closed]: "Closed",
    };
    return transitionIds.map((id) => ({ label: labelsMap[id] || `Status ${id}`, value: id }));
  };

  const getModuleText = (module: TicketModule) => {
    const map: Record<number, string> = {
      [TicketModule.AML]: "AML",
      [TicketModule.Billing]: "Billing",
      [TicketModule.Books]: "Books",
      [TicketModule.CRM]: "CRM",
      [TicketModule.PAYE]: "PAYE",
      [TicketModule.SA]: "SA",
      [TicketModule.Accounts]: "Accounts",
    };
    return map[module] || "N/A";
  };

  const getIssueTypeLabelText = (type: number) => {
    switch (type) {
      case 1: return "Bug";
      case 2: return "Development";
      case 3: return "Enhancement";
      default: return "Unknown";
    }
  };

  const transitions = getAvailableTransitions();
  const isTerminalTicket = [TicketStatus.Closed].includes(ticket.status);

  // Fallback / merge assignee select options
  const assignedObj = ticket?.assignedTo;
  const assignedObjId = assignedObj?.id;
  const assignedObjName = assignedObj?.name || (assignedObj as any)?.email;

  const selectedAssigneeOption = assignedObjId
    ? {
      value: String(assignedObjId),
      label: assignedObjName || `User ${assignedObjId}`,
    }
    : null;

  const assigneeSelectOptions = React.useMemo(() => {
    if (selectedAssigneeOption && !assigneeOptions.some((o) => String(o.value) === String(selectedAssigneeOption.value))) {
      return [selectedAssigneeOption, ...assigneeOptions];
    }
    return assigneeOptions;
  }, [assigneeOptions, selectedAssigneeOption]);

  // Local Confirmations Helpers
  const handleAssignSelect = (userId: string) => {
    const selectedOption = assigneeSelectOptions.find((o) => String(o.value) === String(userId));
    const agentName = selectedOption ? selectedOption.label : `User ${userId}`;
    setTempAgentId(userId);
    setTempAgentName(agentName);
    setActivePopup("assignAgent");
  };

  const handleAssignConfirm = async () => {
    if (tempAgentId) {
      await onAssignChange(tempAgentId);
      setActivePopup(null);
      setTempAgentName("");
    }
  };

  const handleResolutionETASelect = (date: any) => {
    if (!date) {
      onResolutionETAChange(null);
      return;
    }
    setTempEtaDate(dayjs(date).format('DD/MM/YYYY'));
    setActivePopup("eta");
  };

  const handleResolutionETAConfirm = async () => {
    if (tempEtaDate) {
      await onResolutionETAChange(tempEtaDate, etaReason.trim());
      setActivePopup(null);
      setTempEtaDate(null);
      setEtaReason("");
    }
  };

  const handleDueDateSelect = (date: any) => {
    if (!date) {
      onDueDateChange(null);
      return;
    }
    setTempDueDate(dayjs(date).format('DD/MM/YYYY'));
    setActivePopup("dueDate");
  };

  const handleDueDateConfirm = async () => {
    if (tempDueDate) {
      await onDueDateChange(tempDueDate, dueDateReason.trim());
      setActivePopup(null);
      setTempDueDate(null);
      setDueDateReason("");
    }
  };

  const handleInputRequiredToggle = (checked: boolean) => {
    setTempInputChecked(checked);
    setInputRequiredReason("");
    setActivePopup("inputRequired");
  };

  const handleInputRequiredSubmit = async () => {
    if (!inputRequiredReason.trim()) return;
    await onInputRequiredToggle(tempInputChecked, inputRequiredReason.trim());
    setActivePopup(null);
    setInputRequiredReason("");
  };

  const getStatusLabelText = (status: number | null) => {
    if (status === null) return "";
    const map: Record<number, string> = {
      [TicketStatus.Open]: "Open",
      [TicketStatus.InProgress]: "In progress",
      [TicketStatus.UnderResearch]: "Under research",
      [TicketStatus.InTesting]: "Testing",
      [TicketStatus.ReadyForRelease]: "Ready for release",
      [TicketStatus.Closed]: "Closed",
    };
    return map[status] || `Status ${status}`;
  };

  const handleStatusSelect = (nextStatus: number) => {
    setTempStatusValue(nextStatus);
    setStatusReason("");
    setSelectedCloseReason(CloseReason.Unknown);
    setUpdateLinkedTickets(false);
    setActivePopup("status");
  };

  const handleStatusConfirm = async () => {
    const isValid = tempStatusValue === TicketStatus.Closed
      ? (selectedCloseReason !== CloseReason.Unknown && (selectedCloseReason !== CloseReason.Other || statusReason.trim().length >= 5))
      : statusReason.trim().length >= 5;

    if (tempStatusValue !== null && isValid) {
      try {
        await onStatusTransitionConfirm(tempStatusValue, statusReason.trim(), updateLinkedTickets);
        setActivePopup(null);
        setTempStatusValue(null);
        setStatusReason("");
        setSelectedCloseReason(CloseReason.Unknown);
        setUpdateLinkedTickets(false);
      } catch (e) {
        // Handled by parent
      }
    }
  };

  const handleCloseConfirmSubmit = async () => {
    const isValid = selectedCloseReason !== CloseReason.Unknown && (selectedCloseReason !== CloseReason.Other || closeReason.trim().length >= 5);
    if (isValid) {
      try {
        await onCloseConfirm(closeReason.trim(), updateLinkedTickets);
        setActivePopup(null);
        setCloseReason("");
        setSelectedCloseReason(CloseReason.Unknown);
        setUpdateLinkedTickets(false);
      } catch (e) {
        // Handled by parent
      }
    }
  };

  const handleReopenConfirmSubmit = async () => {
    if (reopenReason.trim().length >= 5) {
      try {
        await onReopenConfirm(reopenReason.trim());
        setActivePopup(null);
        setReopenReason("");
      } catch (e) {
        // Handled by parent
      }
    }
  };


  const handlePrioritySelect = (priority: number) => {
    const names: Record<number, string> = { [TicketPriority.P4]: "Low", [TicketPriority.P3]: "Medium", [TicketPriority.P2]: "High", [TicketPriority.P1]: "Urgent" };
    setTempPriorityValue(priority);
    setTempPriorityName(names[priority] || `Priority ${priority}`);
    setActivePopup("priority");
  };

  const handlePriorityConfirm = async () => {
    if (tempPriorityValue !== null) {
      try {
        await onPriorityChange(tempPriorityValue);
        setActivePopup(null);
        setTempPriorityValue(null);
        setTempPriorityName("");
      } catch (e) { }
    }
  };

  const handleModuleSelect = (module: number) => {
    const names: Record<number, string> = {
      1: "AML",
      2: "Billing",
      3: "Books",
      4: "CRM",
      5: "PAYE",
      6: "SA",
      7: "Accounts",
    };
    setTempModuleValue(module);
    setTempModuleName(names[module] || `Module ${module}`);
    setActivePopup("module");
  };

  const handleModuleConfirm = async () => {
    if (tempModuleValue !== null) {
      try {
        await onModuleChange(tempModuleValue);
        setActivePopup(null);
        setTempModuleValue(null);
        setTempModuleName("");
      } catch (e) { }
    }
  };



  const renderFormLabel = (label: string, required = false) => (
    <Text
      strong
      style={{
        display: "block",
        marginBottom: 6,
        fontSize: 12.5,
        color: token.colorTextSecondary,
      }}
    >
      {required && <span style={{ color: token.colorError, marginRight: 4 }}>*</span>}
      {label}
    </Text>
  );

  return (
    <div
      className="ticket-properties-scroll-container"
      style={{
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        border: subtleBorder,
        boxShadow: premiumShadow,
        padding: "20px 18px",
        zIndex: 10,
      }}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        {/* Sidebar Title */}
        <div>
          <Space size={8} style={{ margin: 0 }}>
            <SettingOutlined style={{ color: token.colorPrimary, fontSize: 14 }} />
            <Text
              strong
              style={{
                fontSize: 13,
                color: token.colorTextHeading,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Ticket actions
            </Text>
          </Space>
          {/* <Text type="secondary" style={{ fontSize: 11.5, display: "block" }}>
            Configure dynamic ticket state & assignments
          </Text> */}
        </div>

        {/* Properties Inspector grid */}
        <div
          className="properties-inspector-grid"
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          {/* Ticket Number inline display */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size={6} style={{ color: token.colorTextDescription }}>
              <NumberOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ticket number
              </Text>
            </Space>
            <div>
              <Text strong style={{ fontSize: 13, color: token.colorTextHeading }}>
                #{ticket?.number}
              </Text>
            </div>
          </div>

          {/* Status inline selector */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size={6} style={{ color: token.colorTextDescription }}>
              <TagOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Status
              </Text>
            </Space>
            <div>
              {transitions.length > 0 ? (
                <Popover
                  trigger="click"
                  placement="left"
                  open={activePopup === "status"}
                  onOpenChange={(open) => {
                    if (!open && !submitting) {
                      setActivePopup(null);
                      setTempStatusValue(null);
                      setStatusReason("");
                      setUpdateLinkedTickets(false);
                    }
                  }}
                  content={
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: 280 }}
                    >
                      <Space style={{ color: token.colorPrimary }}>
                        <SyncOutlined spin={submitting} />
                        <Text strong style={{ color: token.colorTextHeading }}>
                          Transition to: {getStatusLabelText(tempStatusValue)}
                        </Text>
                      </Space>
                      <Text
                        style={{
                          fontSize: 12,
                          color: token.colorTextSecondary,
                        }}
                      >
                        Provide a brief explanation detailing why you are
                        updating the ticket state.
                      </Text>
                      {tempStatusValue === TicketStatus.Closed && (
                        <Select
                          placeholder="Select a closed reason"
                          style={{ width: "100%", fontSize: 12 }}
                          options={CLOSED_REASONS}
                          onChange={(val) => {
                            setSelectedCloseReason(val);
                            const opt = CLOSED_REASONS.find((r) => r.value === val);
                            if (val !== CloseReason.Other) {
                              setStatusReason(opt ? opt.label : "");
                            } else {
                              setStatusReason("");
                            }
                          }}
                          value={
                            CLOSED_REASONS.some((r) => r.value === selectedCloseReason)
                              ? selectedCloseReason
                              : undefined
                          }
                          disabled={submitting}
                        />
                      )}
                      {(tempStatusValue !== TicketStatus.Closed || selectedCloseReason === CloseReason.Other) && (
                        <Input.TextArea
                          rows={3}
                          value={statusReason}
                          onChange={(e) => setStatusReason(e.target.value)}
                          placeholder="Please write the reason for this state transition (min 5 characters)..."
                          disabled={submitting}
                          style={{ fontSize: 12, borderRadius: 6 }}
                        />
                      )}
                      {(() => {
                        const linkedDeps = ticket?.dependencies?.filter(d => d.type === TicketDependencyType.Linked) ?? [];
                        if (linkedDeps.length === 0) return null;
                        return (
                          <Checkbox
                            checked={updateLinkedTickets}
                            onChange={(e) => setUpdateLinkedTickets(e.target.checked)}
                          >
                            Update status to all linked tickets.
                          </Checkbox>
                        );
                      })()}
                      <Space
                        style={{ width: "100%", justifyContent: "flex-end" }}
                      >
                        <Button
                          size="small"
                          onClick={() => {
                            setActivePopup(null);
                            setTempStatusValue(null);
                            setStatusReason("");
                            setSelectedCloseReason(CloseReason.Unknown);
                            setUpdateLinkedTickets(false);
                          }}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          loading={submitting}
                          onClick={handleStatusConfirm}
                          disabled={
                            submitting ||
                            (tempStatusValue === TicketStatus.Closed
                              ? selectedCloseReason === CloseReason.Unknown ||
                              (selectedCloseReason === CloseReason.Other &&
                                statusReason.trim().length < 5)
                              : statusReason.trim().length < 5)
                          }
                        >
                          Update
                        </Button>
                      </Space>
                    </Space>
                  }
                >
                  <Select
                    size="middle"
                    value={
                      tempStatusValue !== null ? tempStatusValue : ticket.status
                    }
                    disabled={submitting}
                    style={{
                      fontWeight: 600,
                      width: 140,
                    }}
                    dropdownStyle={{ minWidth: 150 }}
                    onChange={handleStatusSelect}
                    options={[
                      {
                        label: getStatusLabelText(ticket.status),
                        value: ticket.status,
                        disabled: true,
                      },
                      ...transitions.filter(t => t.value !== TicketStatus.Closed).map((t) => ({
                        label: t.label,
                        value: t.value,
                      })),
                    ]}
                  />
                </Popover>
              ) : (
                getStatusTag(ticket.status)
              )}
            </div>
          </div>

          {/* Business Name */}
          {(ticket.company?.name || ticket.company?.value || (ticket as any)?.Company?.Name || (ticket as any)?.Company?.Value) && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Space size={6} style={{ color: token.colorTextDescription }}>
                <ShopOutlined style={{ fontSize: 12 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Business name
                </Text>
              </Space>
              <Text
                style={{
                  fontWeight: 600,
                  fontSize: 12.5,
                  color: token.colorText,
                  maxWidth: "60%",
                  textAlign: "right",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ticket.company?.name || ticket.company?.value || (ticket as any)?.Company?.Name || (ticket as any)?.Company?.Value}
              </Text>
            </div>
          )}

          {/* Raised By */}
          {(ticket.sender?.name || (ticket.sender as any)?.Name || ticket.createdBy?.userName || (ticket.createdBy as any)?.UserName) && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Space size={6} style={{ color: token.colorTextDescription }}>
                <UserOutlined style={{ fontSize: 12 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Raised by
                </Text>
              </Space>
              <Text
                style={{
                  fontWeight: 600,
                  fontSize: 12.5,
                  color: token.colorText,
                  maxWidth: "60%",
                  textAlign: "right",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ticket.sender?.name || (ticket.sender as any)?.Name || ticket.createdBy?.userName || (ticket.createdBy as any)?.UserName}
              </Text>
            </div>
          )}

          {/* Priority */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size={6} style={{ color: token.colorTextDescription }}>
              <AlertOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Priority
              </Text>
            </Space>
            <div>
              <Popconfirm
                title="Change ticket priority"
                description={`Are you sure you want to change the priority to ${tempPriorityName}?`}
                open={activePopup === "priority"}
                onConfirm={handlePriorityConfirm}
                onCancel={() => {
                  setActivePopup(null);
                  setTempPriorityValue(null);
                  setTempPriorityName("");
                }}
                okButtonProps={{ loading: submitting }}
                placement="left"
              >
                <Select
                  size="middle"
                  value={
                    tempPriorityValue !== null
                      ? tempPriorityValue
                      : ticket.priority
                  }
                  disabled={submitting || isTerminalTicket}
                  style={{
                    fontWeight: 600,
                    width: 140,
                  }}
                  onChange={handlePrioritySelect}
                  options={[
                    { label: "Select", value: TicketPriority.Unknown },
                    { label: "Low", value: TicketPriority.P4 },
                    { label: "Medium", value: TicketPriority.P3 },
                    { label: "High", value: TicketPriority.P2 },
                    { label: "Urgent", value: TicketPriority.P1 },
                  ]}
                />
              </Popconfirm>
            </div>
          </div>

          {/* Module */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size={6} style={{ color: token.colorTextDescription }}>
              <AppstoreOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Module
              </Text>
            </Space>
            <div>
              <Popconfirm
                title="Change ticket module"
                description={`Are you sure you want to change the module to ${tempModuleName}?`}
                open={activePopup === "module"}
                onConfirm={handleModuleConfirm}
                onCancel={() => {
                  setActivePopup(null);
                  setTempModuleValue(null);
                  setTempModuleName("");
                }}
                okButtonProps={{ loading: submitting }}
                placement="left"
              >
                <Select
                  size="middle"
                  value={
                    tempModuleValue !== null ? tempModuleValue : ticket.module
                  }
                  disabled={submitting || isTerminalTicket}
                  style={{
                    fontWeight: 600,
                    width: 140,
                  }}
                  onChange={handleModuleSelect}
                  options={[
                    { label: "AML", value: TicketModule.AML },
                    { label: "Billing & subscriptions", value: TicketModule.Billing },
                    { label: "Bookkeeping", value: TicketModule.Books },
                    { label: "CRM", value: TicketModule.CRM },
                    { label: "Payroll", value: TicketModule.PAYE },
                    { label: "Self assessment", value: TicketModule.SA },
                    { label: "Tax and accounts", value: TicketModule.Accounts },
                  ]}
                />
              </Popconfirm>
            </div>
          </div>

          {/* Input Required Switch with Popover Callout */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size={6} style={{ color: token.colorTextDescription }}>
              <InfoCircleOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Input required
              </Text>
            </Space>
            <Popover
              trigger="click"
              placement="bottomRight"
              open={activePopup === "inputRequired"}
              onOpenChange={(open) => {
                if (!open && !submitting) {
                  setActivePopup(null);
                  setInputRequiredReason("");
                }
              }}
              content={
                <Space direction="vertical" size="small" style={{ width: 280 }}>
                  <Text style={{ fontSize: 12 }}>
                    {tempInputChecked
                      ? "Provide the message required from the client."
                      : "Provide a reason for disabling input required."}
                  </Text>
                  <Input.TextArea
                    rows={3}
                    value={inputRequiredReason}
                    onChange={(e) => setInputRequiredReason(e.target.value)}
                    placeholder="Enter message..."
                    disabled={submitting}
                    style={{ fontSize: 12 }}
                  />
                  <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setActivePopup(null);
                        setInputRequiredReason("");
                      }}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      loading={submitting}
                      onClick={handleInputRequiredSubmit}
                      disabled={!inputRequiredReason.trim()}
                    >
                      Submit
                    </Button>
                  </Space>
                </Space>
              }
            >
              <Switch
                checked={ticket.inputRequired}
                onChange={handleInputRequiredToggle}
                disabled={
                  submitting || ticket.inputRequired || isTerminalTicket
                }
              />
            </Popover>
          </div>

          {/* ETA DatePicker with Popconfirm Callout */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size={6} style={{ color: token.colorTextDescription }}>
              <ClockCircleOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Resolution ETA
              </Text>
            </Space>
            <div>
              <Popover
                trigger="click"
                placement="left"
                open={activePopup === "eta"}
                onOpenChange={(open) => {
                  if (open) {
                    setTempEtaDate(ticket?.resolved && ticket.resolved?.resolutionETA ? dayjs(ticket.resolved.resolutionETA).format('DD/MM/YYYY') : null);
                    setActivePopup("eta");
                  } else if (!submitting) {
                    setActivePopup(null);
                    setTempEtaDate(null);
                    setEtaReason("");
                  }
                }}
                content={
                  <Space direction="vertical" size="small" style={{ width: 280 }}>
                    <Text style={{ fontSize: 12 }}>
                      Select resolution ETA and provide reason
                    </Text>
                    <DatePicker
                      size="middle"
                      placeholder="Set ETA"
                      value={tempEtaDate ? dayjs(tempEtaDate, "DD/MM/YYYY") : null}
                      disabled={submitting}
                      disabledDate={(current) => current && current.isBefore(dayjs().startOf("day"))}
                      allowClear={false}
                      style={{ width: "100%" }}
                      minDate={dayjs()}
                      format="DD/MM/YYYY"
                      onChange={(date) => {
                        if (date) setTempEtaDate(dayjs(date).format('DD/MM/YYYY'));
                        else setTempEtaDate(null);
                      }}
                      onBlur={(e: any) => {
                        if (e?.target?.value) {
                          const selectedDay = dayjs(e?.target?.value, DateFormats, true);
                          if (selectedDay.isValid() && !selectedDay.isBefore(dayjs().startOf("day"))) {
                            setTempEtaDate(selectedDay.format("DD/MM/YYYY"));
                          }
                        }
                      }}
                    />
                    {ticket?.resolved && ticket.resolved?.resolutionETA && <Input.TextArea
                      rows={3}
                      value={etaReason}
                      onChange={(e) => setEtaReason(e.target.value)}
                      placeholder={"Enter reason (required)..."}
                      disabled={submitting}
                      style={{ fontSize: 12 }}
                    />}
                    <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setActivePopup(null);
                          setTempEtaDate(null);
                          setEtaReason("");
                        }}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        loading={submitting}
                        onClick={handleResolutionETAConfirm}
                        disabled={!tempEtaDate || (!!(ticket?.resolved && ticket.resolved?.resolutionETA) && !etaReason.trim())}
                      >
                        Submit
                      </Button>
                    </Space>
                  </Space>
                }
              >
                <Button 
                  style={{ width: 140, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} 
                  disabled={submitting || isTerminalTicket}
                >
                  <Text style={{ fontSize: 13, color: ticket?.resolved?.resolutionETA ? 'inherit' : token.colorTextPlaceholder }}>
                    {ticket?.resolved?.resolutionETA ? dayjs(ticket.resolved.resolutionETA).format('DD/MM/YYYY') : "Set ETA"}
                  </Text>
                  <CalendarOutlined style={{ color: token.colorTextPlaceholder }} />
                </Button>
              </Popover>
            </div>
          </div>

          {/* Due Date DatePicker with Popconfirm Callout */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size={6} style={{ color: token.colorTextDescription }}>
              <CalendarOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Due date (SLA plan)
              </Text>
            </Space>
            <div>
              <Popover
                trigger="click"
                placement="left"
                open={activePopup === "dueDate"}
                onOpenChange={(open) => {
                  if (open) {
                    setTempDueDate(ticket?.dueDate ? dayjs(ticket.dueDate).format('DD/MM/YYYY') : null);
                    setActivePopup("dueDate");
                  } else if (!submitting) {
                    setActivePopup(null);
                    setTempDueDate(null);
                    setDueDateReason("");
                  }
                }}
                content={
                  <Space direction="vertical" size="small" style={{ width: 280 }}>
                    <Text style={{ fontSize: 12 }}>
                      Select due date and provide reason
                    </Text>
                    <DatePicker
                      size="middle"
                      placeholder="Set due date"
                      value={tempDueDate ? dayjs(tempDueDate, "DD/MM/YYYY") : null}
                      disabled={submitting}
                      allowClear={false}
                      style={{ width: "100%" }}
                      minDate={dayjs()}
                      format="DD/MM/YYYY"
                      onChange={(date) => {
                        if (date) setTempDueDate(dayjs(date).format('DD/MM/YYYY'));
                        else setTempDueDate(null);
                      }}
                      onBlur={(e: any) => {
                        if (e?.target?.value) {
                          const selectedDay = dayjs(e?.target?.value, DateFormats, true);
                          if (selectedDay.isValid() && !selectedDay.isBefore(dayjs().startOf("day"))) {
                            setTempDueDate(selectedDay.format("DD/MM/YYYY"));
                          }
                        }
                      }}
                    />
                    <Input.TextArea
                      rows={3}
                      value={dueDateReason}
                      onChange={(e) => setDueDateReason(e.target.value)}
                      placeholder={ticket?.dueDate ? "Enter reason (required)..." : "Enter reason (optional)..."}
                      disabled={submitting}
                      style={{ fontSize: 12 }}
                    />
                    <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setActivePopup(null);
                          setTempDueDate(null);
                          setDueDateReason("");
                        }}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        loading={submitting}
                        onClick={handleDueDateConfirm}
                        disabled={!tempDueDate || (!!ticket?.dueDate && !dueDateReason.trim())}
                      >
                        Submit
                      </Button>
                    </Space>
                  </Space>
                }
              >
                <Button 
                  style={{ width: 140, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} 
                  disabled={submitting || isTerminalTicket}
                >
                  <Text style={{ fontSize: 13, color: ticket?.dueDate ? 'inherit' : token.colorTextPlaceholder }}>
                    {ticket?.dueDate ? dayjs(ticket.dueDate).format('DD/MM/YYYY') : "Set due date"}
                  </Text>
                  <CalendarOutlined style={{ color: token.colorTextPlaceholder }} />
                </Button>
              </Popover>
            </div>
          </div>

          {/* Practice Agent */}
          {(ticket.agent?.name || (ticket.agent as any)?.Name) && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Space size={6} style={{ color: token.colorTextDescription }}>
                <UserOutlined style={{ fontSize: 12 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Practice Agent
                </Text>
              </Space>
              <Text
                style={{
                  fontWeight: 600,
                  fontSize: 12.5,
                  color: token.colorText,
                }}
              >
                {ticket.agent?.name || (ticket.agent as any)?.Name}
              </Text>
            </div>
          )}

          {/* Collaborators */}
          {ticket.collaborators && ticket.collaborators.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Space
                size={6}
                style={{ color: token.colorTextDescription, marginTop: 2 }}
              >
                <TeamOutlined style={{ fontSize: 12 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Practice Collaborators
                </Text>
              </Space>
              <div style={{ textAlign: "right", maxWidth: "60%" }}>
                <Tooltip
                  placement="bottom"
                  title={ticket.collaborators
                    .map((c) => c.name)
                    .join(", ")}
                >
                  <Text
                    style={{
                      fontWeight: 600,
                      fontSize: 12.5,
                      color: token.colorText,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {ticket.collaborators.map((c) => c.name).join(", ")}
                  </Text>
                </Tooltip>
              </div>
            </div>
          )}


          {/* Source */}
          {ticket.source && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Space size={6} style={{ color: token.colorTextDescription }}>
                <LinkOutlined style={{ fontSize: 12 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>Source</Text>
              </Space>
              <div style={{ textAlign: "right", maxWidth: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {ticket.source.startsWith("http://") || ticket.source.startsWith("https://") ? (
                  <a
                    href={ticket.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 600, fontSize: 12.5, color: token.colorLink }}
                  >
                    {ticket.source.includes("/business/") ? "Business link" : "View link"}
                  </a>
                ) : (
                  <Text style={{ fontWeight: 600, fontSize: 12.5, color: token.colorText }}>
                    {ticket.source}
                  </Text>
                )}
              </div>
            </div>
          )}

          {/* Issue Type */}
          {ticket.issueType !== undefined && ticket.issueType !== 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Space size={6} style={{ color: token.colorTextDescription }}>
                <SettingOutlined style={{ fontSize: 12 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Type
                </Text>
              </Space>
              <Text
                style={{
                  fontWeight: 600,
                  fontSize: 12.5,
                  color: token.colorText,
                }}
              >
                {getIssueTypeLabelText(ticket.issueType)}
              </Text>
            </div>
          )}

          {/* Raised On (Date and Time) */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size={6} style={{ color: token.colorTextDescription }}>
              <ClockCircleOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Raised on
              </Text>
            </Space>
            <Text
              style={{
                fontWeight: 600,
                fontSize: 12.5,
                color: token.colorText,
              }}
            >
              {ticket.createdBy?.date
                ? dayjs(ticket.createdBy.date).format("DD/MM/YYYY hh:mm A")
                : (ticket as any)?.CreatedBy?.Date
                  ? dayjs((ticket as any).CreatedBy.Date).format("DD/MM/YYYY hh:mm A")
                  : "-"}
            </Text>
          </div>

          {/* Total Open Days */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {ticket.status !== TicketStatus.Closed && <Space size={6} style={{ color: token.colorTextDescription }}>
              <CalendarOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Total open days
              </Text>
            </Space>}
            <Text
              style={{
                fontWeight: 600,
                fontSize: 12.5,
                color: token.colorText,
              }}
            >
              {ticket?.createdBy?.date ? dayjs(ticket?.createdBy?.date).fromNow() : '-'}
            </Text>
          </div>
        </div>

        {/* Assignee support with Popconfirm Callout */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space size={6} style={{ color: token.colorTextDescription }}>
            <UserOutlined style={{ fontSize: 12 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Assignee Support
            </Text>
          </Space>
          <div>
            <Popconfirm
              title="Assign support agent"
              description={`Are you sure you want to assign this ticket to ${tempAgentName}?`}
              open={activePopup === "assignAgent"}
              onConfirm={handleAssignConfirm}
              onCancel={() => {
                setActivePopup(null);
                setTempAgentId(assignedObjId ? String(assignedObjId) : undefined);
                setTempAgentName("");
              }}
              okButtonProps={{ loading: submitting }}
              placement="left"
            >
              <Select
                size="middle"
                placeholder="Select"
                value={tempAgentId !== undefined && tempAgentId !== null ? String(tempAgentId) : (assignedObjId ? String(assignedObjId) : undefined)}
                onChange={handleAssignSelect}
                options={assigneeSelectOptions}
                loading={assigneeLoading}
                disabled={submitting || assigneeLoading || isTerminalTicket}
                style={{ width: 140, fontWeight: 600 }}
                dropdownStyle={{ borderRadius: 6 }}
              />
            </Popconfirm>
          </div>
        </div>

        {/* Collaborators support with Popconfirm Callout */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 12,
          }}
        >
          <Space size={6} style={{ color: token.colorTextDescription }}>
            <TeamOutlined style={{ fontSize: 12 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Collaborators
            </Text>
          </Space>
          <div>
            <Popconfirm
              title="Update Collaborators"
              description={`Are you sure you want to update the collaborators?`}
              open={activePopup === "collaborators"}
              onConfirm={async () => {
                if (onCollaboratorsChange) {
                  await onCollaboratorsChange(tempCollaboratorIds);
                }
                setActivePopup(null);
              }}
              onCancel={() => {
                setActivePopup(null);
                setTempCollaboratorIds(ticket?.supportCollaborators?.map(c => String(c.id)) ?? []);
              }}
              okButtonProps={{ loading: submitting }}
              placement="left"
            >
              <Select
                mode="multiple"
                maxTagCount="responsive"
                size="middle"
                placeholder="Select"
                value={tempCollaboratorIds}
                onChange={(values) => {
                  setTempCollaboratorIds(values);
                  setActivePopup("collaborators");
                }}
                options={assigneeSelectOptions}
                loading={assigneeLoading}
                disabled={submitting || assigneeLoading || isTerminalTicket}
                style={{ width: 140, fontWeight: 600 }}
                dropdownStyle={{ borderRadius: 6 }}
              />
            </Popconfirm>
          </div>
        </div>

        {/* Workflow Actions Section */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          <Text
            type="secondary"
            strong
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: token.colorTextDescription,
            }}
          >
            Actions
          </Text>

          <div
            className="properties-actions-row"
            style={{ display: "flex", gap: 8, width: "100%", flexWrap: "wrap" }}
          >
            <div style={{ flex: 1, minWidth: 120 }}>
              {!isTerminalTicket ? (
                <Popover
                  trigger="click"
                  placement="top"
                  open={activePopup === "close"}
                  onOpenChange={(open) => {
                    if (!open && !submitting) {
                      setActivePopup(null);
                      setCloseReason("");
                    }
                    setUpdateLinkedTickets(false);
                  }}
                  content={
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: 280 }}
                    >
                      <Space style={{ color: token.colorError }}>
                        <CheckCircleOutlined />
                        <Text strong style={{ color: token.colorTextHeading }}>
                          Close ticket
                        </Text>
                      </Space>
                      <Text
                        style={{
                          fontSize: 12,
                          color: token.colorTextSecondary,
                        }}
                      >
                        Are you sure you want to close this ticket?
                      </Text>
                      <Select
                        placeholder="Select reason"
                        style={{ width: "100%", fontSize: 12 }}
                        options={CLOSED_REASONS}
                        onChange={(val) => {
                          setSelectedCloseReason(val);
                          const opt = CLOSED_REASONS.find((r) => r.value === val);
                          if (val !== CloseReason.Other) {
                            setCloseReason(opt ? opt.label : "");
                          } else {
                            setCloseReason("");
                          }
                        }}
                        value={
                          CLOSED_REASONS.some((r) => r.value === selectedCloseReason)
                            ? selectedCloseReason
                            : undefined
                        }
                        disabled={submitting}
                      />
                      {selectedCloseReason === CloseReason.Other && (
                        <Input.TextArea
                          rows={3}
                          value={closeReason}
                          onChange={(e) => setCloseReason(e.target.value)}
                          placeholder="Please write the reason for closing this ticket (min 5 characters)..."
                          disabled={submitting}
                          style={{ fontSize: 12, borderRadius: 6 }}
                        />
                      )}
                      {(() => {
                        const linkedDeps = ticket?.dependencies?.filter(d => d.type === TicketDependencyType.Linked) ?? [];
                        if (linkedDeps.length === 0) return null;
                        return (
                          <Checkbox
                            checked={updateLinkedTickets}
                            onChange={(e) => setUpdateLinkedTickets(e.target.checked)}
                          >
                            Close all linked tickets.
                          </Checkbox>
                        );
                      })()}
                      <Space
                        style={{ width: "100%", justifyContent: "flex-end" }}
                      >
                        <Button
                          size="small"
                          onClick={() => {
                            setActivePopup(null);
                            setCloseReason("");
                            setSelectedCloseReason(CloseReason.Unknown);
                            setUpdateLinkedTickets(false);
                          }}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          danger
                          size="small"
                          loading={submitting}
                          onClick={handleCloseConfirmSubmit}
                          disabled={
                            submitting ||
                            selectedCloseReason === CloseReason.Unknown ||
                            (selectedCloseReason === CloseReason.Other &&
                              closeReason.trim().length < 5)
                          }
                        >
                          Confirm
                        </Button>
                      </Space>
                    </Space>
                  }
                >
                  <Button
                    block
                    type="default"
                    danger
                    disabled={submitting}
                    size="middle"
                    style={{ borderRadius: 6, fontWeight: 500 }}
                    onClick={() => {
                      setCloseReason("");
                      setActivePopup("close");
                    }}
                  >
                    Close ticket
                  </Button>
                </Popover>
              ) : (
                <Popover
                  trigger="click"
                  placement="top"
                  open={activePopup === "reopen"}
                  onOpenChange={(open) => {
                    if (!open && !submitting) {
                      setActivePopup(null);
                      setReopenReason("");
                    }
                  }}
                  content={
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: 280 }}
                    >
                      <Space style={{ color: token.colorPrimary }}>
                        <SyncOutlined spin={submitting} />
                        <Text strong style={{ color: token.colorTextHeading }}>
                          Reopen ticket
                        </Text>
                      </Space>
                      <Text
                        style={{
                          fontSize: 12,
                          color: token.colorTextSecondary,
                        }}
                      >
                        Are you sure you want to reopen this ticket?
                      </Text>
                      <Input.TextArea
                        rows={3}
                        value={reopenReason}
                        onChange={(e) => setReopenReason(e.target.value)}
                        placeholder="Please write the reason for reopening this ticket (min 5 characters)..."
                        disabled={submitting}
                        style={{ fontSize: 12, borderRadius: 6 }}
                      />
                      <Space
                        style={{ width: "100%", justifyContent: "flex-end" }}
                      >
                        <Button
                          size="small"
                          onClick={() => {
                            setActivePopup(null);
                            setReopenReason("");
                          }}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          loading={submitting}
                          onClick={handleReopenConfirmSubmit}
                          disabled={reopenReason.trim().length < 5}
                        >
                          Confirm
                        </Button>
                      </Space>
                    </Space>
                  }
                >
                  <Button
                    block
                    type="default"
                    disabled={submitting}
                    size="middle"
                    style={{ borderRadius: 6, fontWeight: 500 }}
                    onClick={() => {
                      setReopenReason("");
                      setActivePopup("reopen");
                    }}
                  >
                    Reopen Ticket
                  </Button>
                </Popover>
              )}
            </div>

            {/* Unmerge Button */}
            {ticket.status !== TicketStatus.Closed && (() => {
              const mergedDeps = ticket?.dependencies?.filter(d => d.type === TicketDependencyType.Merged) ?? [];
              const hasMerged = mergedDeps.length > 0;
              if (!hasMerged) return null;

              return (
                <div style={{ flex: 1, minWidth: 120 }}>
                  <Popconfirm
                    title="Unmerge Tickets"
                    description="Are you sure you want to unmerge all tickets that were merged into this one?"
                    onConfirm={async () => {
                      if (onUnmergeConfirm) {
                        await onUnmergeConfirm();
                      }
                    }}
                    okText="Yes"
                    cancelText="No"
                    placement="top"
                    okButtonProps={{ loading: submitting }}
                  >
                    <Button
                      block
                      type="default"
                      disabled={submitting}
                      size="middle"
                      style={{ borderRadius: 6, fontWeight: 500 }}
                    >
                      Unmerge Tickets
                    </Button>
                  </Popconfirm>
                </div>
              );
            })()}

            {/* Unlink Button */}
            {(() => {
              const linkedDeps = ticket?.dependencies?.filter(d => d.type === TicketDependencyType.Linked) ?? [];
              const hasLinked = linkedDeps.length > 0;
              if (!hasLinked) return null;

              return (
                <div style={{ flex: 1, minWidth: 120 }}>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: "current",
                          label: "Remove the current ticket from all links",
                          onClick: async () => {
                            if (onUnlinkConfirm) await onUnlinkConfirm(false);
                          },
                        },
                        {
                          key: "all",
                          label: "Remove all interconnected links",
                          onClick: async () => {
                            if (onUnlinkConfirm) await onUnlinkConfirm(true);
                          },
                        },
                      ],
                    }}
                    placement="bottom"
                    disabled={submitting}
                    trigger={["click"]}
                  >
                    <Button
                      block
                      type="default"
                      disabled={submitting}
                      size="middle"
                      style={{ borderRadius: 6, fontWeight: 500 }}
                    >
                      Unlink Tickets <ArrowDownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                    </Button>
                  </Dropdown>
                </div>
              );
            })()}
          </div>
        </div>
      </Space>

      <Drawer
        title="Shift ticket to Capsitask"
        placement="right"
        width={600}
        open={activePopup === "shift"}
        closable={false}
        extra={
          <Button
            type="text"
            icon={<CloseOutlined style={{ fontSize: 16 }} />}
            onClick={() => {
              if (!shifting) {
                setActivePopup(null);
              }
            }}
          />
        }
        onClose={() => {
          if (!shifting) {
            setActivePopup(null);
          }
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              padding: "12px 28px",
            }}
          >
            <Button onClick={() => setActivePopup(null)} disabled={shifting}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={shifting}
              disabled={
                !selectedProjectId ||
                !taskTitle.trim() ||
                selectedTypeIds.length === 0 ||
                selectedAssigneeIds.length === 0 ||
                selectedReportingToIds.length === 0 ||
                !taskDescription.trim()
              }
              onClick={handleShiftConfirm}
            >
              Shift to Capsitask
            </Button>
          </div>
        }
        destroyOnClose
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            padding: "24px 28px",
          }}
        >
          <div>
            {renderFormLabel("Title", true)}
            <Input
              placeholder="Enter task title"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              disabled={shifting}
              style={{ borderRadius: 6 }}
            />
          </div>

          <div>
            {renderFormLabel("Project", true)}
            <Select
              showSearch
              placeholder="Select project"
              loading={loadingProjects}
              style={{ width: "100%" }}
              value={selectedProjectId}
              onChange={handleProjectChange}
              options={capsiProjects}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
              }
              disabled={shifting}
              dropdownStyle={{ borderRadius: 6 }}
            />
          </div>

          <div className="drawer-flex-row" style={{ display: "flex", gap: 20 }}>
            <div style={{ flex: 1 }}>
              {renderFormLabel("Type", true)}
              <Select
                showSearch
                mode="multiple"
                placeholder="Select"
                loading={loadingTypes}
                style={{ width: "100%" }}
                value={selectedTypeIds}
                onChange={setSelectedTypeIds}
                options={capsiTypes}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
                disabled={shifting}
                dropdownStyle={{ borderRadius: 6 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              {renderFormLabel("Priority", true)}
              <Select
                placeholder="Select"
                style={{ width: "100%" }}
                value={taskPriority}
                onChange={setTaskPriority}
                options={[
                  { label: "Select", value: TicketPriority.Unknown },
                  { label: "Low", value: TicketPriority.P4 },
                  { label: "Medium", value: TicketPriority.P3 },
                  { label: "High", value: TicketPriority.P2 },
                  { label: "Urgent", value: TicketPriority.P1 },
                ]}
                disabled={shifting}
                dropdownStyle={{ borderRadius: 6 }}
              />
            </div>
          </div>

          <div className="drawer-flex-row" style={{ display: "flex", gap: 20 }}>
            <div style={{ flex: 1 }}>
              {renderFormLabel("Tags", false)}
              <Select
                showSearch
                mode="multiple"
                placeholder="Select"
                loading={loadingTags}
                style={{ width: "100%" }}
                value={selectedTagIds}
                onChange={setSelectedTagIds}
                options={capsiTags}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
                disabled={shifting}
                dropdownStyle={{ borderRadius: 6 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              {renderFormLabel("Branches", false)}
              <Select
                showSearch
                mode="multiple"
                placeholder="Select"
                loading={loadingBranches}
                style={{ width: "100%" }}
                value={selectedBranchIds}
                onChange={setSelectedBranchIds}
                options={capsiBranches}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
                disabled={shifting}
                dropdownStyle={{ borderRadius: 6 }}
              />
            </div>
          </div>

          <div>
            {renderFormLabel("Assignees", true)}
            <Select
              showSearch
              mode="multiple"
              placeholder={
                selectedProjectId ? "Select" : "Select a project first"
              }
              loading={loadingAssignees}
              style={{ width: "100%" }}
              value={selectedAssigneeIds}
              onChange={setSelectedAssigneeIds}
              options={capsiAssignees}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
              }
              disabled={!selectedProjectId || shifting}
              dropdownStyle={{ borderRadius: 6 }}
            />
          </div>

          <div>
            {renderFormLabel("Reporting to", true)}
            <Select
              showSearch
              mode="multiple"
              placeholder={
                selectedProjectId ? "Select" : "Select a project first"
              }
              loading={loadingAssignees}
              style={{ width: "100%" }}
              value={selectedReportingToIds}
              onChange={setSelectedReportingToIds}
              options={capsiAssignees}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
              }
              disabled={!selectedProjectId || shifting}
              dropdownStyle={{ borderRadius: 6 }}
            />
          </div>

          <div>
            {renderFormLabel("Due date", false)}
            <DatePicker
              style={{ width: "100%", borderRadius: 6 }}
              value={taskDueDate}
              minDate={dayjs()}
              onChange={(date) => setTaskDueDate(date)}
              onBlur={(e: any) => {
                if (e?.target?.value) {
                  const selectedDay = dayjs(e?.target?.value, DateFormats, true);
                  if (selectedDay.isValid() && !selectedDay.isBefore(dayjs().startOf("day"))) {
                    setTaskDueDate(selectedDay);
                  } else setTaskDueDate(dayjs())
                }
              }}
              format="DD/MM/YYYY"
              disabled={shifting}
            />
          </div>

          <div>
            {renderFormLabel("Description", true)}
            <div className="ckeditor-description">
              <CKEditor
                editor={ClassicEditor as any} //@ts-ignore
                data={taskDescription}
                onChange={(event: any, editor: any) => {
                  setTaskDescription(editor.getData());
                }}
                disabled={shifting}
              />
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};
